import {
  ApolloClient,
  ApolloLink,
  type FetchResult,
  InMemoryCache,
  type Observable,
  createHttpLink,
  from,
} from '@apollo/client';
import { onError } from '@apollo/client/link/error';

import { getAccessToken } from '@/shared/utils/token.utils';
import { handleRefreshToken } from './handleRefreshToken';

import { appDataStore } from '@/entities/app-data';
import { setApolloClient } from '@/shared/api/apollo-client';

const httpLink = createHttpLink({
  uri: import.meta.env.VITE_BASE_URL,
  credentials: 'include',
  headers: {
    platform: 'admin',
  },
});

const authMiddleware = new ApolloLink((operation, forward) => {
  const { headers } = operation.getContext();
  const accessToken = getAccessToken();

  const selectedOrganizationUuid =
    appDataStore.state.selectedOrganization?.uuid;
  const selectClientGroupUuid = appDataStore.state.selectedClientGroup?.uuid;

  operation.setContext({
    headers: {
      ...headers,
      ...(accessToken && {
        authorization: `Bearer ${accessToken}`,
      }),
      ...(selectedOrganizationUuid && {
        orgUuid: selectedOrganizationUuid,
      }),
      ...(selectClientGroupUuid && {
        clientGroupUuid: selectClientGroupUuid,
      }),
    },
  });
  return forward(operation);
});

const errorLink = onError(
  ({ graphQLErrors, operation, forward, networkError, response }) => {
    if (graphQLErrors) {
      if (operation.operationName === 'refreshToken') {
        return;
      }

      for (const err of graphQLErrors) {
        const message = err?.message;

        if (message === 'Unauthorized') {
          return handleRefreshToken({
            forward,
            graphQLErrors,
            networkError,
            operation,
            response,
          }) as Observable<FetchResult>;
        }
      }
    }
  },
);

export const client = new ApolloClient({
  link: from([authMiddleware, errorLink, httpLink]),
  cache: new InMemoryCache(),
  defaultOptions: {
    watchQuery: {
      fetchPolicy: 'cache-and-network',
      errorPolicy: 'all',
    },
    query: {
      fetchPolicy: 'network-only',
      errorPolicy: 'all',
    },
    mutate: {
      errorPolicy: 'all',
    },
  },
});

// Register apollo client to shared folder for other lower layers to use
setApolloClient(client);
